import { useCallback, useEffect, useState } from "react";
import {
  getMappedDowntimeClassifications,
  getUnmappedDowntimeClassifications,
} from "@/api/downtime-classification-api.js";
import { getDateOffset } from "@/lib/datetime-utils.js";
import { shouldReportApiError } from "@/api/api-utils.js";
import { useSessionTenant } from "@/contexts/SessionTenantContext.jsx";
import { useError } from "@/contexts/ErrorContext.jsx";
import { classificationErrorDetail } from "@/components/downtime-classification/downtime-classification-utils.js";

/**
 * Unmapped classification row from {@link getUnmappedDowntimeClassifications}
 * (API response field `AlarmMessages`). Same shape as
 * {@link import("@/api/downtime-classification-api.js").UnmappedClassifications}.
 * @typedef {Object} UnmappedClassificationRow
 * @property {string} Message - The alarm message text.
 * @property {string} Vendor - The vendor the mapping applies to.
 * @property {number} DowntimeEventCount - How many downtime events can be attributed to the combination of vendor and message.
 * @property {number} DowntimeHoursCount - The total duration of downtime events attributed to this alarm message in the search range, in hours.
 */

/**
 * Mapped classification row from {@link getMappedDowntimeClassifications}
 * (API response field `AlarmMessageLookups`). Same shape as
 * {@link import("@/api/downtime-classification-api.js").MappedClassifications}.
 * @typedef {Object} MappedClassificationRow
 * @property {number} AlarmMessageLookupId - PK for the classification mapping.
 * @property {string} Message - The alarm message text.
 * @property {string} Vendor - The vendor the mapping applies to.
 * @property {boolean|null|undefined} IsMech - Mech classification: `true`, `false`, or unknown (`null`/`undefined`).
 */

/**
 * Shared fetch/refetch state for downtime classification lists.
 * @typedef {Object} DowntimeClassificationsState
 * @property {UnmappedClassificationRow[]|MappedClassificationRow[]} rows - Classification rows from the API (empty on error or non-array responses).
 * @property {boolean} isLoading - `true` while a fetch is in flight.
 * @property {string|null} error - Local error message when the last fetch failed; otherwise `null`.
 * @property {() => Promise<void>} refetch - Re-run the configured fetcher.
 */

/**
 * Shared fetch/refetch hook for downtime classification lists. Both mapped and unmapped classifications can be retrieved
 * through this.
 * @param {() => Promise<UnmappedClassificationRow[]|MappedClassificationRow[]|null|undefined>} fetcher - Async function that returns the row array (or null/undefined)
 * @param {string} errorMessage - Banner / local error message on failure
 * @returns {DowntimeClassificationsState}
 */
function useDowntimeClassifications(fetcher, errorMessage) {
  const { tenantRefreshKey } = useSessionTenant();
  const { addError } = useError();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(/** @type {string|null} */ (null));

  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await fetcher();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setRows([]);
      setError(errorMessage);
      if (shouldReportApiError(err)) {
        addError(errorMessage, classificationErrorDetail(err));
      }
    } finally {
      setIsLoading(false);
    }
  }, [fetcher, errorMessage, addError]);

  useEffect(() => {
    refetch();
  }, [refetch, tenantRefreshKey]);

  return { rows, isLoading, error, refetch };
}

/**
 * Fetches unmapped downtime classifications for the current tenant, one request
 * per vendor, then concatenates the results. Defaults to the last year.
 * An empty vendor list skips the API and returns no rows.
 * @param {string[]} vendors - Vendor names to query (one API call each)
 * @returns {{
 *   rows: UnmappedClassificationRow[],
 *   isLoading: boolean,
 *   error: string|null,
 *   refetch: () => Promise<void>,
 * }}
 */
export function useUnmappedDowntimeClassifications(vendors) {
  // Stabilize against inline array literals so identity changes do not refetch.
  const vendorsKey = (Array.isArray(vendors) ? vendors : [])
    .map((vendor) => String(vendor ?? "").trim())
    .filter(Boolean)
    .join("\0");

  const fetcher = useCallback(async () => {
    const vendorList = vendorsKey.length === 0 ? [] : vendorsKey.split("\0");
    if (vendorList.length === 0) {
      return [];
    }

    // For now, we default to the last year. In the future, we may want to
    // allow the user to specify a custom date range.
    const endDate = new Date();
    const startDate = getDateOffset(endDate.toISOString(), "365d");
    const start = startDate.toISOString();
    const end = endDate.toISOString();

    const results = await Promise.all(
      vendorList.map((vendor) =>
        getUnmappedDowntimeClassifications(start, end, vendor)
      )
    );

    return results.flatMap((data) => (Array.isArray(data) ? data : []));
  }, [vendorsKey]);

  return useDowntimeClassifications(
    fetcher,
    "Failed to load unmapped downtime classifications."
  );
}

/**
 * Fetches mapped downtime classifications for the current tenant.
 * @returns {{
 *   rows: MappedClassificationRow[],
 *   isLoading: boolean,
 *   error: string|null,
 *   refetch: () => Promise<void>,
 * }}
 */
export function useMappedDowntimeClassifications() {
  const fetcher = useCallback(() => getMappedDowntimeClassifications(), []);

  return useDowntimeClassifications(
    fetcher,
    "Failed to load mapped downtime classifications."
  );
}
